import React, { useRef, useEffect, useMemo, useState } from 'react';
import cn from 'classnames';
import * as PropTypes from 'prop-types';
import { createPortal } from 'react-dom';
import { useBlockScroll } from '../../hooks/useBlockScroll';
import { CSSTransition } from 'react-transition-group';
import { useAuth0 } from '../../context/auth0';

import { ModalCustomerSignup } from '../modals';
import HistoryList from '../HistoryList';
import LanguagesDropdownMobile from './LanguagesDropdownMobile';
import { useClickEventOnDocument } from '../../hooks/useClickEventOnDocument';
import pages from '../../define/pages';
import endpoints from '../../define/endpoints';
import { ROLES } from '../../../constants';
import Accordion, { AccordionStyles } from '../Accordion/Accordion';
import AccordionContainer from '../Accordion/AccordionContainer';
import ShowMore from '../ShowMore/ShowMore';
import CircleCard from '../CircleCard/CircleCard';

import Icon from '@iconify/react';
import iconHeart from '@iconify-icons/mdi/heart-outline';
import iconHistory from '@iconify-icons/mdi/history';
import iconAccount from '@iconify-icons/mdi/account-circle';
import iconTicket from '@iconify-icons/mdi/ticket-confirmation-outline';

import Avatar, { AvatarSizes } from '../Avatar/Avatar';
import Button, {
  ButtonSizes,
  ButtonStyles,
  ButtonWidths,
} from '../Button/Button';
import BetaIcon, { IconColors, IconSizes } from '../Icons/Icon';
import { getGiftIdeas } from '../../hooks/getGiftIdeas';
import SmartSearch from '../SmartSearch/SmartSearch';
import { TRANSLATIONS } from '../../../translations';

let language_fullname = 'Italiano';
let language_code = freedomeConfig.baseLocale;

freedomeConfig.languages.map((language, index) => {
  if (language.code == freedomeConfig.locale)
    language_fullname = language.fullname;
  language_code = freedomeConfig.locale;
});

const ViewMobile = (props) => {
  const { isAuthenticated, isLoading, loginWithRedirect, logout, user } =
    useAuth0();

  useBlockScroll(props.isOpen);

  const isSpanishEnv = freedomeConfig.baseLocale === 'es';

  const [isLanguagesDropdownOpen, setIsLanguagesDropdownOpen] = useState(false);
  const giftIdeas = useMemo(() => getGiftIdeas(isSpanishEnv), []);
  const popularCategories = props.mostPopularCategories;
  const baseUrl = window.freedomeConfig.endpoints.base_url;

  const transitionProps = {
    classNames: 'navigation-menu-mbl',
    in: props.isOpen && !isLoading,
    mountOnEnter: true,
    unmountOnExit: true,
    appear: true,
    timeout: 200,
  };

  useClickEventOnDocument([isLanguagesDropdownOpen], () => {
    isLanguagesDropdownOpen && setIsLanguagesDropdownOpen(false);
  });

  const LanguagesSection = () => {
    return <LanguagesDropdownMobile isOpen={isLanguagesDropdownOpen} />;
  };

  const categoryAccordionMake = (c) => {
    if (c.categories.length === 0) {
      return null;
    }
    let label;
    let icon;
    switch (c.parent_id) {
      case 1: //water
        label = app_main_wpml.water;
        icon = <BetaIcon name={'water'} size={IconSizes[20]} />;
        break;
      case 2: //earth
        label = app_main_wpml.earth;
        icon = <BetaIcon name={'earth'} size={IconSizes[20]} />;
        break;
      case 3: //air
        label = app_main_wpml.air;
        icon = <BetaIcon name={'air'} size={IconSizes[20]} />;
        break;
      case 4: //fire
        label = app_main_wpml.fire;
        icon = <BetaIcon name={'fire'} size={IconSizes[20]} />;
        break;
      case 5: //snow
        label = app_main_wpml.snow;
        icon = <BetaIcon name={'snow'} size={IconSizes[20]} />;
        break;
    }
    return (
      <>
        <Accordion
          key={c.parent_id}
          style={AccordionStyles.secondary}
          leftIcon={icon}
          label={label}
        >
          <ShowMore itemsToDisplay={circleCardsFromCategories(c.categories)} />
        </Accordion>
      </>
    );
  };

  const circleCardsFromCategories = (categories) => {
    return categories?.map((c) => {
      return (
        <CircleCard
          key={c.id}
          caption={c.title}
          link={`${baseUrl}/${c.slug}/`}
          image={c.thumbnail_image}
        />
      );
    });
  };

  return createPortal(
    <CSSTransition {...transitionProps}>
      <div className={cn('navigation-menu-mbl')}>
        <div className={cn('navigation-menu-mbl__header')}>
          <div
            className={cn(
              'navigation-menu-mbl__header__icon-wrapper navigation-menu-mbl__header__icon-wrapper--logo'
            )}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="36"
              height="36"
              viewBox="0 0 36 36"
              fill="none"
            >
              <rect width="36" height="36" fill="white" fillOpacity="0.01" />
              <path
                d="M34 11.7047C34 11.7047 32.8576 7.18717 29.7049 7.18717C24.9486 7.18717 20.8487 15.9798 20.8487 15.9798C20.8487 15.9798 19.3034 5.0345 14.1906 1.75051C9.07778 -1.53271 0 1.75128 0 1.75128L19.1538 19.4837H14.8447L5.44062 34.2915C5.44062 34.2915 21.3362 25.0342 25.2253 22.475C33.1599 17.2544 29.7049 11.7047 29.7049 11.7047H34Z"
                fill="#00BF59"
              />
            </svg>
          </div>
          <div className={cn('navigation-menu-mbl__header__form-wrapper')}>
            <SmartSearch isNavSearch={true} />
          </div>

          <div
            className={'navigation-menu-mbl__header__icon-wrapper'}
            onClick={props.onClickClose}
          >
            <BetaIcon
              name={'close'}
              size={IconSizes[16]}
              color={IconColors.ghost}
              key={'close'}
            />
          </div>
        </div>

        <div className={cn('navigation-menu-mbl__body')}>
          {localStorage.getItem('listing-history') && (
            <React.Fragment>
              <a
                href={pages.historyList}
                className={cn(
                  'navigation-menu-mbl__body__entry navigation-menu-mbl__body__entry--no-change'
                )}
              >
                <Icon icon={iconHistory} className="icon" />{' '}
                {app_main_wpml.search_history}
              </a>

              <HistoryList
                showAsSwiper={true}
                itemClassName="swiper-slide"
                data-gtm-track_impression
                data-item_list_name="history"
              />
            </React.Fragment>
          )}

          <div className={cn('navigation-menu-mbl__accordion-container')}>
            <AccordionContainer>
              <Accordion
                isInitiallyOpen={true}
                label={app_main_wpml.all_activities}
                leftIcon={<BetaIcon name="compass" size={IconSizes[24]} />}
              >
                <AccordionContainer>
                  <Accordion
                    isInitiallyOpen={true}
                    style={AccordionStyles.secondary}
                    leftIcon={<BetaIcon name="star" size={IconSizes[20]} />}
                    label={app_main_wpml.most_popular}
                  >
                    <ShowMore
                      itemsToDisplay={circleCardsFromCategories(
                        popularCategories
                      )}
                    />
                  </Accordion>
                  {Array.isArray(props.categoriesByPopularity) &&
                    props.categoriesByPopularity.length > 0 ? (
                    props.categoriesByPopularity.map((c) =>
                      categoryAccordionMake(c)
                    )
                  ) : (
                    <></>
                  )}
                </AccordionContainer>
              </Accordion>
              {freedomeConfig.locale != 'en' && (
                <Accordion
                  label={app_main_wpml.gift_ideas}
                  leftIcon={<BetaIcon name="gift" size={IconSizes[24]} />}
                >
                  <ShowMore
                    itemsToDisplay={giftIdeas.map((g) => (
                      <CircleCard
                        key={g.title}
                        caption={g.title}
                        link={`${baseUrl}/${g.url}/`}
                        image={`${endpoints.bucket}gift-landings/${g.url}.jpg`}
                      />
                    ))}
                  />
                  <div className="dropdown__giftcard">
                    <h6>{TRANSLATIONS.GIFTCARD_BOX_TITLE}</h6>
                    <p>
                      <strong>{TRANSLATIONS.GIFTCARD_TITLE}</strong>
                      <br />
                      {TRANSLATIONS.GIFTCARD_BOX_DESCRIPTION}
                    </p>
                    <Button
                      style={ButtonStyles.primary}
                      label={TRANSLATIONS.GIFTCARD_BOX_CTA}
                      size={ButtonSizes.xs}
                      redirect={`${baseUrl}${TRANSLATIONS.GIFTCARD_LINK}/`}
                    />
                  </div>
                </Accordion>
              )}
              <Accordion
                label={app_main_wpml.became_partner}
                leftIcon={<BetaIcon name="partner" size={IconSizes[24]} />}
                onHeadClick={() => {
                  window.location.href = pages.partnership;
                }}
              />
              <Accordion
                label={app_main_wpml.support_center}
                leftIcon={<BetaIcon name="cuca" size={IconSizes[24]} />}
                onHeadClick={() => {
                  window.location.href = pages.contactUs;
                }}
              ></Accordion>
              <Accordion
                label={language_fullname}
                leftIcon={
                  <BetaIcon
                    name={`${language_code}Lang`}
                    size={IconSizes[24]}
                  />
                }
              >
                {freedomeConfig.languages &&
                  freedomeConfig.languages.map((language, index) => {
                    return (
                      <Accordion
                        key={index}
                        style={AccordionStyles.secondary}
                        label={language.fullname}
                        leftIcon={
                          <BetaIcon
                            key={language.code}
                            name={`${language.code}Lang`}
                            size={IconSizes[20]}
                          />
                        }
                        onHeadClick={() => {
                          window.location.href = language.url;
                        }}
                      />
                    );
                  })}
              </Accordion>
              {isAuthenticated && (
                <Accordion
                  label={app_main_wpml.logout}
                  leftIcon={<BetaIcon name="logout" size={IconSizes[24]} />}
                  onHeadClick={logout}
                ></Accordion>
              )}
            </AccordionContainer>
          </div>

          <LanguagesSection />
        </div>

        <>
          {
            <div className={cn('navigation-menu-mbl__footer')}>
              <div className={cn('navigation-menu-mbl__footer__content')}>
                {!isAuthenticated && (
                  <div className="navigation-menu-mbl__footer__content__buttons">
                    <Button
                      label={app_main_wpml.login}
                      style={ButtonStyles.ghost}
                      size={ButtonSizes.isFull}
                      onClick={() => loginWithRedirect()}
                    ></Button>

                    <ModalCustomerSignup />
                  </div>
                )}
                {isAuthenticated && (
                  <div className="grid-x navigation-menu-mbl__footer__content__icons-nav">
                    {!!pages.profile && (
                      <div className="cell auto">
                        <a
                          href={
                            user.role === ROLES.CUSTOMER
                              ? pages.profile
                              : window.freedomeConfig.endpoints.dashboard
                          }
                          className={cn(
                            'navigation-menu-mbl__body__entry navigation-menu-mbl__body__entry--footer'
                          )}
                        >
                          {!user.profile_image && (
                            <Icon icon={iconAccount} className="icon" />
                          )}
                          {!!user.profile_image && (
                            <Avatar
                              size={AvatarSizes.xxs}
                              className={'m-auto'}
                              firstName={user && user.first_name}
                              lastName={user && user.last_name}
                              profileImage={user && user.profile_image}
                            />
                          )}
                          {app_main_wpml.profile}
                        </a>
                      </div>
                    )}
                    {!!pages.orderList && (
                      <div className="cell auto">
                        <a
                          href={pages.orderList}
                          className={cn(
                            'navigation-menu-mbl__body__entry navigation-menu-mbl__body__entry--footer'
                          )}
                        >
                          <Icon icon={iconTicket} className="icon" />
                          {app_main_wpml.reservations}
                        </a>
                      </div>
                    )}
                    {!!pages.wishList && (
                      <div className="cell auto">
                        <a
                          href={pages.wishList}
                          className={cn(
                            'navigation-menu-mbl__body__entry navigation-menu-mbl__body__entry--footer'
                          )}
                        >
                          <Icon icon={iconHeart} className="icon" />
                          {app_main_wpml.favourite}
                        </a>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          }
        </>
      </div>
    </CSSTransition>,
    document.body
  );
};

ViewMobile.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClickClose: PropTypes.func.isRequired,
};

export default ViewMobile;
